/**
 * ReceivableColumns
 *
 * Column definitions for receivable (client) statement tables
 */
import React from 'react';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import VerticalDate from '@components/Dates/VerticalDate';
import Money from '@components/Gui/Money';
import { NwEditButton } from '@components/Gui/NwButton';
import ProjectTitle from '@components/Project/ProjectTitle';
import { SubjectStatementStatusCell } from '@components/StatementRowsCommon/cells';
import TransactionTitle from '@components/Transactions/TransactionTitle';

import { isMainFee } from './ReceivableHelpers';
import { ClientNameInTitle } from './ReceivableStyles';
import ReceivableTotalColumn from './ReceivableTotalColumn';

/**
 * Get columns configuration for receivable statements
 */
export const getReceivableColumns = ({
    contactType,
    showSubjectNameInTitle,
    expand,
    isItemExpanded,
    handleEditTransaction,
    handlePreviewProject,
    handlePreviewService,
    onStatusInvoiceClose,
}) => {
    const columns = [
        {
            title: () => <div style={{ textAlign: 'center' }}>Date</div>,
            dataIndex: 'Date',
            key: 'Date',
            onCell: (record) => ({
                onClick: () => handleEditTransaction(record.Transaction),
                style: { cursor: 'pointer' },
            }),
            render: (text, record) => {
                if (isMainFee(record)) {
                    return (
                        <div className="date-container">
                            <VerticalDate
                                size="small"
                                cancelled={record.Transaction.WorkDateCancelled}
                                date={record.Transaction.RefDate}
                                verticalPadding="0"
                            />
                        </div>
                    );
                } else {
                    return <></>;
                }
            },
        },
        {
            title: () => <div style={{ textAlign: 'center' }}>Client Status</div>,
            dataIndex: 'ClientInvoiceStatus',
            key: 'ClientInvoiceStatus',
            // Niente onClick sulla cella: l'InvoiceDrawer è renderizzato qui dentro e i click nel portal risalirebbero fino al td
            render: (text, record) => {
                return (
                    <SubjectStatementStatusCell
                        statement={record}
                        statementDirection="receivable"
                        additionalText={
                            record.isCommission ? 'Agency Fee' : record.isClientAdvance ? 'Client Advance' : 'Fee'
                        }
                        onInvoiceClose={onStatusInvoiceClose}
                    />
                );
            },
        },
        {
            title: 'Title',
            key: 'Title',
            width: '20%',
            onCell: (record) => ({
                onClick: () => handleEditTransaction(record.Transaction),
                style: { cursor: 'pointer' },
            }),
            render: (record) => {
                if (isMainFee(record)) {
                    return (
                        <div className="transaction-details">
                            {showSubjectNameInTitle && record.ClientName && (
                                <ClientNameInTitle>{record.ClientName}</ClientNameInTitle>
                            )}
                            {record.Transaction.ModelID && <p>{getContactName(record.Transaction.ModelID)}</p>}
                            <TransactionTitle transaction={record.Transaction} />
                            {record.Transaction.ExternalFee && record.Transaction.ExternalFee > 0 ? (
                                <small>
                                    MODEL FEE:{' '}
                                    <Money
                                        amount={record.Transaction.ExternalFee}
                                        currency={record.Transaction.Currency}
                                    />
                                </small>
                            ) : null}
                        </div>
                    );
                } else {
                    return (
                        <div className="transaction-details transaction-commission-name">
                            <small>{record.isClientAdvance ? 'client advance / payroll' : 'agency fee'}</small>
                        </div>
                    );
                }
            },
        },
        {
            title: 'Project',
            dataIndex: 'Project',
            key: 'Project',
            width: '35%',
            className: 'top-aligned',
            onCell: (record) => {
                if (isMainFee(record) && record.Transaction.Project) {
                    return {
                        onClick: (e) => {
                            e.stopPropagation();
                            handlePreviewProject(record.Transaction.Project.ID);
                        },
                        style: { cursor: 'pointer' },
                    };
                }
                return {};
            },
            render: (text, record) => {
                if (isMainFee(record)) {
                    return (
                        <div className="project-details">
                            {record.Transaction.ServiceID && (
                                <p className="transaction-client">{getContactName(record.Transaction.ServiceID)}</p>
                            )}
                            {record.Transaction.Project && (
                                <ProjectTitle project={record.Transaction.Project} compact pointer noClientClick />
                            )}
                        </div>
                    );
                } else {
                    if (record.TransactionPerc) {
                        return <p className="commission-perc">{record.TransactionPerc}%</p>;
                    } else {
                        return <></>;
                    }
                }
            },
        },
    ];

    // Add Job Amount column only if contactType is not 'agency'
    if (contactType !== 'agency') {
        columns.push({
            title: 'Job Amount',
            dataIndex: 'Numbers',
            key: 'Numbers',
            align: 'right',
            width: '10%',
            onCell: (record) => ({
                onClick: () => handleEditTransaction(record.Transaction),
                style: { cursor: 'pointer' },
            }),
            render: (text, record) => {
                if (isMainFee(record) && record.Transaction.Family === 'JobFee') {
                    return (
                        <div className="transaction-gross">
                            {(record.Transaction.Quantity !== 1 ||
                                !['Flat', 'Units'].includes(record.Transaction.TypeQuantity)) && (
                                <p>
                                    <Money
                                        amount={record.Transaction.SingleFee}
                                        currency={record.Transaction.Currency}
                                    />
                                    &nbsp;{`x ${record.Transaction.Quantity} ${record.Transaction.TypeQuantity}`}
                                </p>
                            )}
                            <p>
                                <Money
                                    highlightCurrency
                                    amount={record.Transaction.TotalFee}
                                    currency={record.Transaction.Currency}
                                />
                            </p>
                        </div>
                    );
                } else {
                    return <></>;
                }
            },
        });
    }

    // Client Amount column
    columns.push({
        title: 'Client Amount',
        key: 'Amount',
        width: '15%',
        align: 'right',
        onCell: (record) => ({
            onClick: () => handleEditTransaction(record.Transaction),
            style: { cursor: 'pointer' },
        }),
        render: (record) => {
            return (
                <div className="statement-total">
                    <ReceivableTotalColumn
                        statement={record}
                        transaction={record.Transaction}
                        isExpanded={isItemExpanded(record.ID) || expand}
                    />
                </div>
            );
        },
    });

    // Actions column
    columns.push({
        title: () => <div style={{ textAlign: 'center' }}>Actions</div>,
        dataIndex: 'Actions',
        key: 'Actions',
        render: (text, record) => {
            if (isMainFee(record)) {
                return (
                    <div className="transaction-actions">
                        <NwEditButton
                            iconOnly
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditTransaction(record.Transaction);
                            }}
                        />
                    </div>
                );
            }
            return <></>;
        },
    });

    return columns;
};
